import { Component, OnInit, signal, inject, WritableSignal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Department } from 'src/app/features/department/department.model';
import { Organization } from 'src/app/features/organization/organization-models';
import { UpdateChargingUserAddress } from '../../../charging-user.model';
import { toObservable } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TopLoaderService } from 'src/app/core/services/top-loader.service';
import { PageEvent } from '@angular/material/paginator';
import { combineLatest, firstValueFrom } from 'rxjs';
import { Address } from 'src/app/shared/models/address';
import { AddressService } from 'src/app/core/services/address.service';
import { CreateUserWithoutInvitationRequest } from '../../charging-user-create.model';
import { IdTagUserType } from 'src/app/shared/enums/id-tag-user-type.enum';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ChargingUserService } from 'src/app/shared/services/charging-user.service';
import { FeedbackInfoComponent, SnackbarService } from 'ok-angular-shared/components';
import { UserInvitationService } from 'src/app/shared/services/user.invitation.service';
import { InputMaskModule } from '@ngneat/input-mask';
import { createMask } from '@ngneat/input-mask';
import { ArrayValidators } from 'src/app/shared/validators/array.validator';
import { AccessGroupService } from 'src/app/shared/services/access-group.service';
import { AccessGroup, AccessGroupSearchEvent } from 'src/app/features/access-groups/access-group.model';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { CountryCodeSelectorComponent } from 'src/app/shared/components/country-code-selector/country-code-selector.component';
import { PhoneNumberInputComponent } from 'src/app/shared/components/phone-number-input/phone-number-input.component';
import { AccessGroupCarType } from 'src/app/shared/enums/access-group-car-type';
import { AdmOrgTypeaheadComponent } from 'src/app/shared/components/adm-org-typeahead/adm-org-typeahead.component';
import { AdmDepTypeaheadComponent } from 'src/app/shared/components/adm-dep-typeahead/adm-dep-typeahead.component';
import { MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatInput } from '@angular/material/input';
import { ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import {
  AddressChanged,
  DepAddressTypeaheadComponent,
} from 'src/app/shared/components/dep-address-typeahead/dep-address-typeahead.component';
import { Guid } from 'guid-typescript';

interface CreateChargingUserIdTagWithoutInvitationModalModel {
  organization: FormControl<Organization | null>;
  department: FormControl<Department | null>;
  email: FormControl<string | null>;
  selectedAccessGroups: FormControl<SelectedAccessGroup[]>;
  name: FormControl<string | null>;
  deiveryAddress: FormControl<string | null>;
  phoneNumber: FormControl<string>;
  userHaveOrganizationCar: FormControl<boolean | null>;
  countryCode: FormControl<number>;
  darId: FormControl<Guid | null>;
}
interface SelectedAccessGroup {
  identifier: string;
  name: string;
}
@Component({
  selector: 'adm-create-charging-user-id-tag-without-invitation.modal',
  templateUrl: './create-charging-user-id-tag-without-invitation.modal.component.html',
  styleUrl: './create-charging-user-id-tag-without-invitation.modal.component.scss',
  imports: [
    AdmDepTypeaheadComponent,
    InputMaskModule,
    CustomDropDownArrowDirective,
    CountryCodeSelectorComponent,
    PhoneNumberInputComponent,
    AdmOrgTypeaheadComponent,
    FeedbackInfoComponent,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatIcon,
    MatButton,
    MatFormField,
    MatLabel,
    MatError,
    MatSelect,
    MatSelectTrigger,
    MatOption,
    MatRadioGroup,
    MatRadioButton,
    MatInput,
    ReactiveFormsModule,
    AsyncPipe,
    DepAddressTypeaheadComponent,
  ],
})
export class CreateChargingUserIdTagWithoutInvitationModalComponent implements OnInit {
  data = inject<{
    organization;
  }>(MAT_DIALOG_DATA);
  private loadingService = inject(TopLoaderService);
  dialogRef = inject<MatDialogRef<CreateChargingUserIdTagWithoutInvitationModalComponent>>(MatDialogRef);
  private chargingUserService = inject(ChargingUserService);
  private userInvitationService = inject(UserInvitationService);
  private snackbarService = inject(SnackbarService);
  private addressService = inject(AddressService);
  private accessGroupService = inject(AccessGroupService);

  public userFormGroup: FormGroup<CreateChargingUserIdTagWithoutInvitationModalModel> =
    new FormGroup<CreateChargingUserIdTagWithoutInvitationModalModel>({
      organization: new FormControl<Organization | null>(null, [Validators.required, this.organizationIdValidator]),
      department: new FormControl<Department | null>(null, [Validators.required]),
      selectedAccessGroups: new FormControl<SelectedAccessGroup[]>({ value: [], disabled: true }, { nonNullable: true }),
      email: new FormControl<string | null>(null),
      name: new FormControl<string | null>(null),
      deiveryAddress: new FormControl<string | null>(null),
      phoneNumber: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
      userHaveOrganizationCar: new FormControl<boolean | null>(false),
      countryCode: new FormControl<number>(45, { nonNullable: true }),
      darId: new FormControl<Guid | null>(null, [Validators.required]),
    });
  resetOrgTrigger: WritableSignal<{ reset: boolean }> = signal<{ reset: boolean }>({ reset: false });
  resetDepartmentTrigger: WritableSignal<{ reset: boolean }> = signal<{ reset: boolean }>({ reset: false });
  departmentsDisabled = signal<boolean>(true);

  public accessGroups = signal<AccessGroup[]>([]);
  public accessGroups$ = toObservable(this.accessGroups);

  customerNumberInputMask = createMask({
    rightAlign: false,
    alias: 'numeric',
    placeholder: '',
    min: 0,
    max: 9999999999,
  });

  ngOnInit(): void {
    combineLatest([this.userFormGroup.controls.userHaveOrganizationCar.valueChanges]).subscribe(x => {
      if (this.userFormGroup.controls.organization?.value) {
        this.userFormGroup.controls.selectedAccessGroups.setValue([]);
        this.getAccessGroups(this.userFormGroup.controls.organization.value);
      }
    });

    this.userFormGroup.controls.selectedAccessGroups.setValidators(ArrayValidators.minLength(1));
    this.userFormGroup.controls.selectedAccessGroups.updateValueAndValidity();
  }

  public enableDepartmentDropdown() {
    this.userFormGroup.controls.department.enable();
  }
  public organizationSelected(organization: Organization | null) {
    this.userFormGroup.patchValue({
      organization: organization,
      department: null,
    });
    this.resetDepartmentTrigger.set({ reset: true });
    if (organization && !organization.idTagGroupName) {
      this.snackbarService.error(
        'Der er ikke tilknyttet en id tag gruppe til organisationen. Derfor kan man ikke oprette bruger med brik til denne organisation'
      );
      this.userFormGroup.controls.department.disable();
      return;
    }
    if (organization) {
      this.getAccessGroups(organization);
      this.userFormGroup.controls.department.setValue(null);
      this.departmentsDisabled.set(false);
    } else {
      this.departmentsDisabled.set(true);
    }
  }

  public selectAddress(address: AddressChanged | null) {
    if (address && address.address !== null && address.address !== undefined) {
      const addressFormatted = this.formatAddressToString(address.address);
      this.userFormGroup.patchValue({
        deiveryAddress: addressFormatted,
        darId: address.darId ?? null,
      });
    }
  }

  public formatAddressToString(address: UpdateChargingUserAddress | undefined): string {
    if (!address) {
      return '';
    }
    return this.addressService.formatAddressToString(address as Address);
  }

  public resetOrganization() {
    this.userFormGroup.patchValue({
      organization: null,
      department: null,
    });
    this.resetDepartmentTrigger.set({ reset: true });
    this.departmentsDisabled.set(true);

    this.userFormGroup.patchValue({
      organization: null,
      department: null,
    });
  }

  departmentSelected(department: Department | null) {
    this.userFormGroup.patchValue({
      department: department,
    });
  }

  public resetDepartment() {
    this.userFormGroup.patchValue({
      department: null,
    });
  }
  public getErrorMessage(controlName: string) {
    switch (controlName) {
      case 'alias':
        return 'Indtast venglist et ID';
      case 'emailAddress':
        return 'Indtast venligst en valid email adresse';
      case 'name':
        return 'Indtast venligst navn';
      case 'selectedRole':
        return 'Vælg venligst en rolle';
      default:
        return '';
    }
  }
  public async save() {
    this.loadingService.setLoading(true);
    const organization = this.userFormGroup.controls.organization.value;
    const department = this.userFormGroup.controls.department.value;
    const darId = this.userFormGroup.controls.darId.value;
    const phoneNumber = this.userFormGroup.controls.phoneNumber.value;
    const countryCode = this.userFormGroup.controls.countryCode.value;
    if (!organization || !department || !darId || !phoneNumber || countryCode == null) {
      this.loadingService.setLoading(false);
      return;
    }

    const model = new CreateUserWithoutInvitationRequest();
    model.deliveryAddress = this.userFormGroup.controls.deiveryAddress.value ?? '';
    model.organizationId = organization.id;
    model.departmentId = department.id;
    model.email = this.userFormGroup.controls.email.value ?? '';
    model.name = this.userFormGroup.controls.name.value ?? '';
    model.darId = darId.toString();
    model.phoneNumber = phoneNumber;
    model.accessGroupIdentifiers = this.userFormGroup.controls.selectedAccessGroups.value.map(x => x.identifier);
    model.phoneCountryCode = countryCode;
    model.userType = this.userFormGroup.controls.userHaveOrganizationCar.value ? IdTagUserType.CompanyCar : IdTagUserType.NoCompanyCar;

    this.userInvitationService.createUserWithoutInvitation(model).subscribe({
      next: async (chargingUserId: number) => {
        const chargingUser = await firstValueFrom(this.chargingUserService.getChargingUser(chargingUserId));
        this.dialogRef.close(chargingUser);
        this.snackbarService.success(`Bruger oprettet`);
      },
      error: (httpError: HttpErrorResponse) => {
        if (httpError.status == HttpStatusCode.BadRequest) {
          switch (httpError.error?.code) {
            case 101100:
              this.snackbarService.error('Mobilnummeret angivet er ikke et gyldigt telefonnummer for den indtastede landekode.');
              break;
            case 101101:
              this.snackbarService.error('Mobilnummeret er af et ugyldigt format for den indtastede landekode.');
              break;
            default:
              this.snackbarService.error(httpError.error?.message);
              break;
          }
        } else {
          this.snackbarService.error(`Der er sket en ukendt fejl: (${httpError.error?.message})`);
        }
      },
    });
  }

  private getAccessGroups(organization: Organization) {
    const pageEvent = new PageEvent();
    pageEvent.pageSize = 1000;
    const searchEvent: AccessGroupSearchEvent = new AccessGroupSearchEvent(pageEvent);
    searchEvent.organizationId = organization.id;
    searchEvent.carType = this.userFormGroup.controls.userHaveOrganizationCar.value
      ? AccessGroupCarType.CompanyCar
      : AccessGroupCarType.PrivateCar;
    this.accessGroupService.getAccessGroups(searchEvent).subscribe({
      next: result => {
        this.accessGroups.set(result.items);
        this.userFormGroup.controls.selectedAccessGroups.enable();
      },
    });
  }
  public organizationIdValidator(control: AbstractControl): ValidationErrors | null {
    const org = control.value as Organization | null;
    if (!org || !org.id) {
      return { organizationIdRequired: true };
    }
    return null;
  }
}
